// Heat Pump Service Report template — structured from the Sterio Renewables
// Formaloo form, with "Long Text" replaced by "Remarks".
//
// Field types: check | number | text | select | remarks
//
// withToggleRemarks() automatically inserts a "Remarks" text field after every
// toggle (check) question, so remarks are always available under each toggle
// without having to hand-write them in the definitions.

export function withToggleRemarks(sections) {
  return sections.map((sec) => {
    const fields = [];
    sec.fields.forEach((f, i) => {
      fields.push(f);
      if (f.type === "check") {
        const next = sec.fields[i + 1];
        if (!next || next.type !== "remarks") {
          fields.push({ type: "remarks", id: `${f.id}_remarks`, label: "Remarks" });
        }
      }
    });
    return { ...sec, fields };
  });
}

const RAW_SERVICE_SECTIONS = [
  {
    id: "mechanical",
    title: "Mechanical Check",
    description: "Inspect and service the mechanical components of the system.",
    fields: [
      { type: "check", id: "mech_y_strainer", label: "Isolate the Y strainer, remove filter and clean" },
      { type: "check", id: "mech_pipes_lagging", label: "Check integrity of pipes and lagging" },
      { type: "check", id: "mech_magnetic_filter", label: "Check and clean magnetic particle filter (if fitted)" },
      { type: "check", id: "mech_outdoor_clean", label: "Inspect and clean outdoor unit (brush fins with soft dry brush)" },
      { type: "check", id: "mech_oil_leaks", label: "Check outdoor unit for visual oil leaks (possible refrigerant leak)" },
      { type: "check", id: "mech_refrigerant_leaks", label: "Check for refrigerant leaks" },
      { type: "check", id: "mech_system_pressure", label: "Check system pressure (if unvented) and adjust if necessary" },
      { type: "check", id: "mech_glycol", label: "Check glycol levels and adjust if necessary" },
      { type: "text", id: "glycol_level", label: "Glycol level (record %)", placeholder: "e.g. 28%" },
      { type: "check", id: "mech_3port_valve", label: "Check operation of 3-port valve" },
      { type: "check", id: "mech_room_stat", label: "Check operation of the room thermostat" },
      { type: "check", id: "mech_electrical", label: "Check all electrical connections incl. mains isolator" },
      { type: "check", id: "mech_voltage", label: "Check unit operation voltage and record" },
      { type: "number", id: "operation_voltage", label: "Operation voltage", unit: "V" },
      { type: "check", id: "mech_booster_heater", label: "Check booster heater" },
      { type: "check", id: "mech_rcd", label: "Check RCD operation" },
      { type: "remarks", id: "mech_remarks", label: "Section remarks / notes" },
    ],
  },
  {
    id: "system",
    title: "System Check",
    description: "Record key system readings.",
    fields: [
      { type: "number", id: "flow_rate", label: "Flow rate (recorded)", unit: "L/min" },
      { type: "remarks", id: "flow_rate_remarks", label: "Remarks" },
      { type: "number", id: "heat_meter", label: "Heat meter reading (if fitted)", unit: "kWh" },
      { type: "remarks", id: "heat_meter_remarks", label: "Remarks" },
    ],
  },
  {
    id: "heating_mode",
    title: "Performance — Heating Mode",
    description: "Performance readings taken in heating mode.",
    fields: [
      { type: "number", id: "heating_od_temp", label: "Outdoor temp", unit: "°C" },
      { type: "number", id: "heating_water_out", label: "Water out", unit: "°C" },
      { type: "number", id: "heating_water_in", label: "Water in", unit: "°C" },
      { type: "number", id: "heating_delta_t", label: "Delta T", unit: "°C" },
      { type: "check", id: "heating_compressor_speed", label: "Check compressor speed" },
      { type: "number", id: "heating_comp_hz", label: "Compressor frequency", unit: "Hz" },
      { type: "remarks", id: "heating_remarks", label: "Section remarks / notes" },
    ],
  },
  {
    id: "dhw_mode",
    title: "Performance — DHW Mode",
    description: "Performance readings taken in domestic hot water mode.",
    fields: [
      { type: "number", id: "dhw_od_temp", label: "Outdoor temp", unit: "°C" },
      { type: "number", id: "dhw_water_out", label: "Water out", unit: "°C" },
      { type: "number", id: "dhw_water_in", label: "Water in", unit: "°C" },
      { type: "number", id: "dhw_delta_t", label: "Delta T", unit: "°C" },
      { type: "check", id: "dhw_compressor_speed", label: "Check compressor speed" },
      { type: "number", id: "dhw_comp_hz", label: "Compressor frequency", unit: "Hz" },
      { type: "remarks", id: "dhw_remarks", label: "Section remarks / notes" },
    ],
  },
  {
    id: "heating_system",
    title: "Heating System Checks",
    description: "Verify the wider heating system operation.",
    fields: [
      { type: "check", id: "hsc_emitters", label: "Check all heat emitters / UFH functioning correctly" },
      { type: "check", id: "hsc_flowrate", label: "Check adequate flow rate to emitters / UFH" },
      { type: "check", id: "hsc_stats", label: "Check stats working and set up correctly" },
      { type: "check", id: "hsc_immersion", label: "Check DHW immersion is working" },
      { type: "check", id: "hsc_expansion", label: "Check expansion of heating and DHW system" },
      { type: "text", id: "heating_expansion_vessel", label: "Heating expansion vessel", placeholder: "e.g. 1.0 bar" },
      { type: "text", id: "dhw_expansion_vessel", label: "DHW expansion vessel", placeholder: "e.g. 3.0 bar" },
      { type: "remarks", id: "hsc_remarks", label: "Section remarks / notes" },
    ],
  },
];

export const SERVICE_SECTIONS = withToggleRemarks(RAW_SERVICE_SECTIONS);

export const FINAL_STATUS_OPTIONS = [
  "Pass",
  "Pass with advisories",
  "Follow-up required",
  "Fail",
];

export function fieldLabel(id) {
  for (const s of SERVICE_SECTIONS) {
    const f = s.fields.find((x) => x.id === id);
    if (f) return f;
  }
  return null;
}
